const BASE = "/api";
let authToken = null;
let refreshToken = null;

async function request(path, options = {}) {
  const headers = { "Content-Type": "application/json", ...options.headers };
  if (authToken && path !== "/employee/login") {
    headers["Authorization"] = `Bearer ${authToken}`;
  }

  const res = await fetch(`${BASE}${path}`, { ...options, headers });

  // Auto-refresh token on 401
  if (res.status === 401 && refreshToken && path !== "/employee/login" && path !== "/employee/refresh") {
    const refreshRes = await fetch(`${BASE}/employee/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    if (refreshRes.ok) {
      const data = await refreshRes.json();
      authToken = data.accessToken;
      refreshToken = data.refreshToken;
      headers["Authorization"] = `Bearer ${authToken}`;
      const retry = await fetch(`${BASE}${path}`, { ...options, headers });
      if (!retry.ok) { const err = await retry.json().catch(() => ({})); throw new Error(err.error || "Request failed"); }
      return retry.json();
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || "Request failed");
  }
  return res.json();
}

export const api = {
  request,
  // ─── Manager endpoints ───────────────────────
  getAuthStatus: () => request("/auth/status"),
  getTasks: (s, e) => request(`/tasks?startDate=${s}&endDate=${e}`),
  createTask: (d) => request("/tasks", { method: "POST", body: JSON.stringify(d) }),
  deleteTask: (id) => request(`/tasks/${id}`, { method: "DELETE" }),
  assignTask: (id, eid) => request(`/tasks/${id}/assign`, { method: "POST", body: JSON.stringify({ cleaner_id: eid }) }),
  unassignTask: (id) => request(`/tasks/${id}/unassign`, { method: "POST" }),
  pushWeek: (s, e) => request("/push-week", { method: "POST", body: JSON.stringify({ startDate: s, endDate: e }) }),
  pushDay: (d) => request(`/push-day/${d}`, { method: "POST" }),

  // Calendar & Schedule
  getCalendar: (s, e) => request(`/calendar?startDate=${s}&endDate=${e}`),
  getCleanerSchedule: (s, e) => request(`/cleaner-schedule?startDate=${s}&endDate=${e}`),
  getPlanner: (s, e) => request(`/planner?startDate=${s}&endDate=${e}`),

  // Employees
  getCleaners: () => request("/cleaners"),
  getEmployees: () => request("/employees"),
  addEmployee: (d) => request("/employees", { method: "POST", body: JSON.stringify(d) }),
  updateEmployee: (id, d) => request(`/employees/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  // Backwards compat
  addCleaner: (d) => request("/cleaners", { method: "POST", body: JSON.stringify(d) }),
  updateCleaner: (id, d) => request(`/cleaners/${id}`, { method: "PUT", body: JSON.stringify(d) }),

  // Locations
  getLocations: () => request("/locations"),
  addLocation: (d) => request("/locations", { method: "POST", body: JSON.stringify(d) }),
  updateLocation: (id, d) => request(`/locations/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  deleteLocation: (id) => request(`/locations/${id}`, { method: "DELETE" }),
  deleteEmployee: (id) => request(`/employees/${id}`, { method: "DELETE" }),

  // Shifts (manual)
  getShifts: (s, e) => request(`/shifts?startDate=${s}&endDate=${e}`),
  createShift: (d) => request("/shifts", { method: "POST", body: JSON.stringify(d) }),
  updateShift: (id, d) => request(`/shifts/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  deleteShift: (id) => request(`/shifts/${id}`, { method: "DELETE" }),
  publishShifts: (startDate, endDate) => request("/shifts/publish", { method: "POST", body: JSON.stringify({ startDate, endDate }) }),

  // Settings
  getSettings: () => request("/settings"),
  updateSettings: (d) => request("/settings", { method: "PUT", body: JSON.stringify(d) }),

  // Leave Management
  getLeave: (params) => request(`/leave?${new URLSearchParams(params||{})}`),
  createLeave: (d) => request("/leave", { method: "POST", body: JSON.stringify(d) }),
  updateLeave: (id, d) => request(`/leave/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  deleteLeave: (id) => request(`/leave/${id}`, { method: "DELETE" }),

  // Leave Types
  getLeaveTypes: () => request("/leave-types"),
  createLeaveType: (d) => request("/leave-types", { method: "POST", body: JSON.stringify(d) }),
  updateLeaveType: (id, d) => request(`/leave-types/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  deleteLeaveType: (id) => request(`/leave-types/${id}`, { method: "DELETE" }),

  // Leave Balances
  getLeaveBalances: (empId, year) => request(`/leave-balances/${empId}?year=${year||new Date().getFullYear()}`),
  updateLeaveBalance: (d) => request("/leave-balances", { method: "PUT", body: JSON.stringify(d) }),

  // Public Holidays
  getHolidays: (year) => request(`/public-holidays?year=${year||new Date().getFullYear()}`),
  createHoliday: (d) => request("/public-holidays", { method: "POST", body: JSON.stringify(d) }),
  updateHoliday: (id, d) => request(`/public-holidays/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  deleteHoliday: (id) => request(`/public-holidays/${id}`, { method: "DELETE" }),

  // Documents
  getDocuments: (empId) => request(`/documents${empId?"?employeeId="+empId:""}`),
  createDocument: (d) => request("/documents", { method: "POST", body: JSON.stringify(d) }),
  updateDocument: (id, d) => request(`/documents/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  deleteDocument: (id) => request(`/documents/${id}`, { method: "DELETE" }),
  getExpiringDocuments: () => request("/documents/expiring"),

  // Roles
  getRoles: () => request("/roles"),
  createRole: (d) => request("/roles", { method: "POST", body: JSON.stringify(d) }),
  updateRole: (id, d) => request(`/roles/${id}`, { method: "PUT", body: JSON.stringify(d) }),

  // Shift Conflicts
  checkShiftConflict: (d) => request("/shifts/check-conflict", { method: "POST", body: JSON.stringify(d) }),

  // Shift Trades
  getShiftTrades: () => request("/shift-trades"),
  createShiftTrade: (d) => request("/shift-trades", { method: "POST", body: JSON.stringify(d) }),
  updateShiftTrade: (id, d) => request(`/shift-trades/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  deleteShiftTrade: (id) => request(`/shift-trades/${id}`, { method: "DELETE" }),

  // Audit Log
  getAuditLog: (params) => request(`/audit-log?${new URLSearchParams(params||{})}`),

  // DIER Templates
  getDierTemplates: () => request("/dier-templates"),
  applySundayCredits: (year) => request("/apply-sunday-credits", { method: "POST", body: JSON.stringify({ year }) }),

  // Document Notifications
  getDocumentNotifications: () => request("/documents/notifications"),

  // Payroll
  getPayrollExport: (month, year, opts={}) => {
    let url = `/payroll-export?month=${month}&year=${year}`;
    if(opts.payStart) url += `&payStart=${opts.payStart}&payEnd=${opts.payEnd}`;
    if(opts.otStart) url += `&otStart=${opts.otStart}&otEnd=${opts.otEnd}`;
    if(opts.lvStart) url += `&lvStart=${opts.lvStart}&lvEnd=${opts.lvEnd}`;
    return request(url);
  },

  // Sync
  syncCloudbeds: (s, e) => request("/sync/cloudbeds", { method: "POST", body: JSON.stringify({ startDate: s, endDate: e }) }),
  syncConnecteamCleaners: () => request("/sync/connecteam-cleaners", { method: "POST" }),
  getStats: () => request("/stats"),

  // ─── Employee/Cleaner app endpoints ──────────
  employeeLogin: async (username, password) => {
    const res = await request("/employee/login", { method: "POST", body: JSON.stringify({ username, password }) });
    if (res.accessToken) { authToken = res.accessToken; refreshToken = res.refreshToken; }
    return res;
  },
  employeeShifts: (id, date) => request(`/employee/shifts/${id}${date ? "?date=" + date : ""}`),
  employeeWeek: (id) => request(`/employee/week/${id}`),
  employeeClockIn: (d) => request("/employee/clock-in", { method: "POST", body: JSON.stringify(d) }),
  employeeClockOut: (d) => request("/employee/clock-out", { method: "POST", body: JSON.stringify(d) }),
  employeePing: (d) => request("/employee/location-ping", { method: "POST", body: JSON.stringify(d) }),
  employeeStartTask: (d) => request("/employee/start-task", { method: "POST", body: JSON.stringify(d) }),
  employeeCompleteTask: (d) => request("/employee/complete-task", { method: "POST", body: JSON.stringify(d) }),
  employeeUncompleteTask: (d) => request("/employee/uncomplete-task", { method: "POST", body: JSON.stringify(d) }),
  employeeChangePassword: (d) => request("/employee/change-password", { method: "POST", body: JSON.stringify(d) }),
  employeeTimesheet: (id) => request(`/employee/timesheet/${id}`),
  timesheetExport: (s, e, empId) => request(`/employee/timesheet-export?startDate=${s}&endDate=${e}${empId ? "&employeeId=" + empId : ""}`),
  timesheetMonthly: (empId, month, year) => request(`/employee/timesheet-monthly/${empId}?month=${month}&year=${year}`),
  employeeRequestLeave: (d) => request("/employee/leave-request", { method: "POST", body: JSON.stringify(d) }),
  employeeMyLeave: (id) => request(`/employee/my-leave/${id}`),
  employeeUpdateProfile: (id, data) => request(`/employee/profile/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  getAnnouncements: () => request("/announcements"),
  createAnnouncement: (d) => request("/announcements", { method: "POST", body: JSON.stringify(d) }),
  deleteAnnouncement: (id) => request(`/announcements/${id}`, { method: "DELETE" }),
  getEmployeeAnnouncements: (empId) => request(`/employee/announcements/${empId}`),
  getCoworkers: (empId, start, end) => request(`/employee/coworkers/${empId}${start?"?start="+start+"&end="+end:""}`),
  getSuppliers: () => request("/suppliers"),
  getDashboardStats: () => request("/dashboard-stats"),
  getAttendanceReport: (start, end) => request(`/reports/attendance?start=${start}&end=${end}`),
  addTaskNote: (taskId, note, employeeId) => request(`/tasks/${taskId}/note`, { method: "POST", body: JSON.stringify({ note, employee_id: employeeId }) }),
  getTaskNotes: (taskId) => request(`/tasks/${taskId}/notes`),
  editTaskNote: (taskId, index, text, employeeId) => request(`/tasks/${taskId}/notes/${index}`, { method: "PUT", body: JSON.stringify({ text, employee_id: employeeId }) }),
  deleteTaskNote: (taskId, index, employeeId) => request(`/tasks/${taskId}/notes/${index}`, { method: "DELETE", body: JSON.stringify({ employee_id: employeeId }) }),
  parseSchedule: (image, weekStart) => request("/schedule/parse", { method: "POST", body: JSON.stringify({ image, weekStart }) }),
  importSchedule: (shifts) => request("/schedule/import", { method: "POST", body: JSON.stringify({ shifts }) }),
  markAnnouncementRead: (id, employee_id) => request(`/announcements/${id}/read`, { method: "POST", body: JSON.stringify({ employee_id }) }),
  getAnnouncementReads: (id) => request(`/announcements/${id}/reads`),
  getEmployeeStats: (id, start, end) => request(`/employee-stats/${id}?start=${start||""}&end=${end||""}`),
  updateTaskNote: (id, note, senderName) => request(`/tasks/${id}/note`, { method: "PUT", body: JSON.stringify({ note, sender_name: senderName }) }),
  createSupplier: (d) => request("/suppliers", { method: "POST", body: JSON.stringify(d) }),
  updateSupplier: (id, d) => request(`/suppliers/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  deleteSupplier: (id) => request(`/suppliers/${id}`, { method: "DELETE" }),
  getEmployeeSuppliers: () => request("/employee/suppliers"),
  getEmployeeLeaveTypes: () => request("/employee/leave-types"),
  markAnnouncementRead: (id, empId) => request(`/employee/announcements/${id}/read`, { method: "POST", body: JSON.stringify({ employee_id: empId }) }),
  employeeCancelLeave: (id) => request(`/employee/leave-request/${id}`, { method: "DELETE" }),
  employeeMyDocuments: (id) => request(`/employee/my-documents/${id}`),
  employeeAddDocument: (d) => request("/employee/my-documents", { method: "POST", body: JSON.stringify(d) }),

  // Inspection templates & responses
  getInspectionTemplates: () => request("/inspection-templates"),
  createInspectionTemplate: (d) => request("/inspection-templates", { method: "POST", body: JSON.stringify(d) }),
  updateInspectionTemplate: (id, d) => request(`/inspection-templates/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  deleteInspectionTemplate: (id) => request(`/inspection-templates/${id}`, { method: "DELETE" }),
  getInspectionResponses: (params) => request(`/inspection-responses?${new URLSearchParams(params||{})}`),
  employeeGetInspectionTemplate: (taskId) => request(`/employee/inspection-template/${taskId}`),
  employeeSubmitInspection: (d) => request("/employee/inspection-submit", { method: "POST", body: JSON.stringify(d) }),

  // Handover notes
  getHandoverNotes: (days) => request(`/handover-notes${days?"?days="+days:""}`),
  createHandoverNote: (d) => request("/handover-notes", { method: "POST", body: JSON.stringify(d) }),
  deleteHandoverNote: (id) => request(`/handover-notes/${id}`, { method: "DELETE" }),
  employeeGetHandover: (empId) => request(`/employee/handover-notes/${empId}`),
  employeeCreateHandover: (d) => request("/employee/handover-notes", { method: "POST", body: JSON.stringify(d) }),
  employeeMarkHandoverRead: (id, empId) => request(`/employee/handover-notes/${id}/read`, { method: "POST", body: JSON.stringify({ employee_id: empId }) }),
  employeeMarkHandoverReadAll: (empId) => request(`/employee/handover-notes/read-all`, { method: "POST", body: JSON.stringify({ employee_id: empId }) }),

  // SOP Library
  getSops: () => request("/sops"),
  createSop: (d) => request("/sops", { method: "POST", body: JSON.stringify(d) }),
  updateSop: (id, d) => request(`/sops/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  deleteSop: (id) => request(`/sops/${id}`, { method: "DELETE" }),
  employeeGetSops: () => request("/employee/sops"),

  // Labour Cost
  getLabourCost: (start, end) => request(`/labour-cost?start=${start}&end=${end}`),

  // Overtime confirmation (token-based, no auth)
  getOvertimeCheck: (entryId, token) => request(`/employee/overtime-check/${entryId}?t=${token}`),
  confirmOvertime: (entryId, token, action) => request(`/employee/overtime-confirm/${entryId}`, { method: "POST", body: JSON.stringify({ token, action }) }),

  // Default cleaner auto-assign
  getDefaultCleaner: () => request("/default-cleaner"),
  setDefaultCleaner: (id) => request("/default-cleaner", { method: "PUT", body: JSON.stringify({ default_cleaner_id: id }) }),
  assignAllUnassigned: () => request("/assign-all-unassigned", { method: "POST" }),

  // Permissions & digest
  getMe: (empId) => request(`/me/${empId}`),
  updatePermissions: (empId, d) => request(`/employees/${empId}/permissions`, { method: "PUT", body: JSON.stringify(d) }),
  sendMorningDigestNow: () => request("/morning-digest/send", { method: "POST" }),
  previewMorningDigest: (empId) => request(`/morning-digest/preview/${empId}`),

  // Telegram Health Check
  getTelegramHealth: () => request("/telegram/health"),
  testTelegram: (empId) => request(`/telegram/test/${empId}`, { method: "POST" }),

  // Duplicate Tasks
  getDuplicateTasks: () => request("/tasks/duplicates"),
  resolveDuplicates: (keepId, cancelIds) => request("/tasks/duplicates/resolve", { method: "POST", body: JSON.stringify({ keep_id: keepId, cancel_ids: cancelIds }) }),

  // Backwards compat aliases
  cleanerLogin: async (u, p) => api.employeeLogin(u, p),
  cleanerShifts: (id, d) => api.employeeShifts(id, d),
  cleanerWeek: (id) => api.employeeWeek(id),
  cleanerClockIn: (d) => api.employeeClockIn(d),
  cleanerClockOut: (d) => api.employeeClockOut(d),
  cleanerPing: (d) => api.employeePing(d),
  cleanerCompleteTask: (d) => api.employeeCompleteTask(d),
  cleanerUncompleteTask: (d) => api.employeeUncompleteTask(d),

  // Manager tracking
  getLiveTracking: () => request("/employee/live-tracking"),
  getClockHistory: (id, s, e) => request(`/employee/clock-history/${id}?startDate=${s||""}&endDate=${e||""}`),

  // Properties & Zones
  getProperties: () => request("/properties"),
  getZones: (property_id) => request(`/properties/${property_id}/zones`),
  createProperty: (d) => request("/properties", { method: "POST", body: JSON.stringify(d) }),
  updateProperty: (id, d) => request(`/properties/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  deleteProperty: (id) => request(`/properties/${id}`, { method: "DELETE" }),
  createZone: (d) => request("/property-zones", { method: "POST", body: JSON.stringify(d) }),
  updateZone: (id, d) => request(`/property-zones/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  deleteZone: (id) => request(`/property-zones/${id}`, { method: "DELETE" }),

  // Maintenance (manager)
  getMaintenance: (params) => request(`/maintenance?${new URLSearchParams(params||{})}`),
  createMaintenance: (d) => request("/maintenance", { method: "POST", body: JSON.stringify(d) }),
  claimMaintenance: (id, empId) => request(`/maintenance/${id}/claim`, { method: "POST", body: JSON.stringify({ employee_id: empId }) }),
  resolveMaintenance: (id, d) => request(`/maintenance/${id}/resolve`, { method: "POST", body: JSON.stringify(d) }),
  updateMaintenance: (id, d) => request(`/maintenance/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  deleteMaintenance: (id) => request(`/maintenance/${id}`, { method: "DELETE" }),

  // Maintenance (employee)
  employeeGetProperties: () => request("/employee/properties"),
  employeeSubmitReport: (d) => request("/employee/maintenance-report", { method: "POST", body: JSON.stringify(d) }),
  employeeGetFeed: () => request("/employee/maintenance-feed"),
  employeeGetMyMaintenance: (id) => request(`/employee/my-maintenance/${id}`),
  employeeClaimJob: (d) => request("/employee/maintenance-claim", { method: "POST", body: JSON.stringify(d) }),
  employeeResolveJob: (d) => request("/employee/maintenance-resolve", { method: "POST", body: JSON.stringify(d) }),
  employeeGetTrades: (empId) => request(`/employee/shift-trades${empId?"?employee_id="+empId:""}`),
  employeeClaimTrade: (id, employee_id) => request(`/employee/shift-trades/${id}/claim`, { method: "POST", body: JSON.stringify({ employee_id }) }),
  employeeCancelTrade: (id, employee_id) => request(`/employee/shift-trades/${id}?employee_id=${employee_id}`, { method: "DELETE" }),
  changePassword: (employee_id, new_password) => request("/employee/change-password", { method: "POST", body: JSON.stringify({ employee_id, new_password }) }),
  getMe: (employee_id) => request(`/employee/me/${employee_id}`),

  // ═══ STOCK MODULE ═══
  // Perishables
  getPerishables: () => request("/stock/perishables"),
  addPerishable: (d) => request("/stock/perishables", { method: "POST", body: JSON.stringify(d) }),
  updatePerishable: (id, d) => request(`/stock/perishables/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  deletePerishable: (id) => request(`/stock/perishables/${id}`, { method: "DELETE" }),
  adjustPerishable: (id, d) => request(`/stock/perishables/${id}/adjust`, { method: "POST", body: JSON.stringify(d) }),
  getPerishableMovements: (id) => request(`/stock/perishables/${id}/movements`),
  // Alert recipients
  getStockAlertRecipients: () => request("/stock/alert-recipients"),
  setStockAlertRecipients: (employee_ids) => request("/stock/alert-recipients", { method: "PUT", body: JSON.stringify({ employee_ids }) }),
  // Laundry types
  getLaundryTypes: () => request("/stock/laundry-types"),
  addLaundryType: (d) => request("/stock/laundry-types", { method: "POST", body: JSON.stringify(d) }),
  updateLaundryType: (id, d) => request(`/stock/laundry-types/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  deleteLaundryType: (id) => request(`/stock/laundry-types/${id}`, { method: "DELETE" }),
  adjustLaundryStock: (id, d) => request(`/stock/laundry-types/${id}/adjust`, { method: "POST", body: JSON.stringify(d) }),
  moveLaundryStock: (d) => request("/stock/laundry-move", { method: "POST", body: JSON.stringify(d) }),
  // Batches
  getBatches: (status) => request(`/stock/batches${status?"?status="+status:""}`),
  createBatch: (d) => request("/stock/batches", { method: "POST", body: JSON.stringify(d) }),
  receiveBatch: (id, d) => request(`/stock/batches/${id}/receive`, { method: "POST", body: JSON.stringify(d) }),
  // Damages
  getDamages: (days) => request(`/stock/damages${days?"?days="+days:""}`),
  reportDamage: (d) => request("/stock/damages", { method: "POST", body: JSON.stringify(d) }),
  deleteDamage: (id, employee_id) => request(`/stock/damages/${id}${employee_id?"?employee_id="+employee_id:""}`, { method: "DELETE" }),
  // Unit sets
  getUnitSets: () => request("/stock/unit-sets"),
  saveUnitSet: (d) => request("/stock/unit-sets", { method: "POST", body: JSON.stringify(d) }),
  deleteUnitSet: (id) => request(`/stock/unit-sets/${id}`, { method: "DELETE" }),
  // Laundry forecast
  getLaundryForecast: () => request("/stock/laundry-forecast"),
  // Equipment
  getEquipment: () => request("/stock/equipment"),
  addEquipment: (d) => request("/stock/equipment", { method: "POST", body: JSON.stringify(d) }),
  updateEquipment: (id, d) => request(`/stock/equipment/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  deleteEquipment: (id) => request(`/stock/equipment/${id}`, { method: "DELETE" }),
  moveEquipment: (id, d) => request(`/stock/equipment/${id}/move`, { method: "POST", body: JSON.stringify(d) }),
  adjustEquipment: (id, d) => request(`/stock/equipment/${id}/adjust`, { method: "POST", body: JSON.stringify(d) }),

  // ─── Hotel / Cloudbeds ───
  getCloudbedsAccounts: () => request("/cloudbeds/accounts"),
  addCloudbedsAccount: (d) => request("/cloudbeds/accounts", { method: "POST", body: JSON.stringify(d) }),
  updateCloudbedsAccount: (id, d) => request(`/cloudbeds/accounts/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  deleteCloudbedsAccount: (id, employee_id) => request(`/cloudbeds/accounts/${id}?employee_id=${employee_id}`, { method: "DELETE" }),
  testCloudbedsAccount: (id) => request(`/cloudbeds/accounts/${id}/test`, { method: "POST" }),
  getCloudbedsRooms: (id) => request(`/cloudbeds/accounts/${id}/rooms`),
  getCloudbedsMappings: (id) => request(`/cloudbeds/accounts/${id}/mappings`),
  addCloudbedsMapping: (id, d) => request(`/cloudbeds/accounts/${id}/mappings`, { method: "POST", body: JSON.stringify(d) }),
  deleteCloudbedsMapping: (accountId, mappingId, employee_id) => request(`/cloudbeds/accounts/${accountId}/mappings/${mappingId}?employee_id=${employee_id}`, { method: "DELETE" }),
  getHotelServices: () => request("/hotel/services"),
  updateHotelService: (id, d) => request(`/hotel/services/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  getAssignedProperties: (id) => request(`/employees/${id}/assigned-properties`),
  setAssignedProperties: (id, property_ids) => request(`/employees/${id}/assigned-properties`, { method: "PUT", body: JSON.stringify({ property_ids }) }),
  syncAllHotels: () => request("/hotel/sync", { method: "POST" }),
  syncHotelAccount: (accountId) => request(`/hotel/sync/${accountId}`, { method: "POST" }),
  getHotelTasks: (start_date, end_date, extra = {}) => {
    const p = new URLSearchParams({ start_date, end_date, ...extra });
    return request(`/hotel/tasks?${p.toString()}`);
  },
  getHotelCalendar: (start_date, end_date) => request(`/hotel/calendar?start_date=${start_date}&end_date=${end_date}`),
  claimHotelTask: (id) => request(`/hotel/tasks/${id}/claim`, { method: "POST" }),
  unclaimHotelTask: (id) => request(`/hotel/tasks/${id}/unclaim`, { method: "POST" }),
  startHotelTask: (id) => request(`/hotel/tasks/${id}/start`, { method: "POST" }),
  completeHotelTask: (id) => request(`/hotel/tasks/${id}/complete`, { method: "POST" }),
  updateHotelTask: (id, d) => request(`/hotel/tasks/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  createHotelTask: (d) => request("/hotel/tasks", { method: "POST", body: JSON.stringify(d) }),
  deleteHotelTask: (id) => request(`/hotel/tasks/${id}`, { method: "DELETE" }),
};
